'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Sidebar from './Sidebar';
import ProfileSettingsModal from './ProfileSettingsModal';
import NotificationBell from './NotificationBell';
import MessageCenter from './MessageCenter';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { subscribeToRealtimeEvents } from '@/lib/realtimeSubscriptions';
import { createClient } from '@/lib/supabase/client';
import OnboardingWizard, { shouldShowOnboarding } from './OnboardingWizard';


interface AppLayoutProps {
  children: React.ReactNode;
  role?: 'sender' | 'customer' | 'traveler' | 'host' | 'admin' | 'station_moderator' | 'student' | 'courier';
}

const LANG_KEY = 'muyu_lang_pref';
const THEME_KEY = 'muyu_theme_pref';

export default function AppLayout({ children, role = 'customer' }: AppLayoutProps) {
  const { user, signOut, getUserProfile } = useAuth();
  const router = useRouter();
  const { language, setLanguage, t, supportedLanguages } = useLanguage();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(language);
  const [signingOut, setSigningOut] = useState(false);
  const [isDark, setIsDark] = useState(false);
  // Profile modal state
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalTab, setProfileModalTab] = useState<'profile' | 'settings'>('profile');

  // Onboarding wizard state
  const [showOnboarding, setShowOnboarding] = useState(false);

  // User profile data
  const [userProfile, setUserProfile] = useState<{ full_name: string; avatar_url: string; role: string } | null>(null);

  const langRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  // ── Init theme & language from localStorage ──────────────────────────────
  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_KEY);
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const dark = savedTheme === 'dark' || (!savedTheme && prefersDark);
    setIsDark(dark);
    document.documentElement.classList.toggle('dark', dark);

    setSelectedLang(language);
  }, [language]);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem(THEME_KEY, next ? 'dark' : 'light');
  };

  const handleLangSelect = (code: string) => {
    setSelectedLang(code);
    setLangOpen(false);
    setLanguage(code as any);
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) {
      getUserProfile()
        .then((profile) => {
          if (profile) setUserProfile(profile);
        })
        .catch(() => {});
    }
  }, [user]);

  // Show onboarding wizard once per user after login
  useEffect(() => {
    if (user?.id) {
      setShowOnboarding(shouldShowOnboarding(user.id));
    }
  }, [user?.id]);

  // ── Listen for avatar updates emitted from ProfileDashboardContent ──────────
  useEffect(() => {
    const handler = (e: Event) => {
      const url = (e as CustomEvent<string>).detail;
      setUserProfile((prev) => prev ? { ...prev, avatar_url: url } : prev);
    };
    window.addEventListener('avatar-updated', handler);
    return () => window.removeEventListener('avatar-updated', handler);
  }, []);

  // ── Listen for profile name/field updates emitted from ProfileDashboardContent ──
  useEffect(() => {
    const handler = (e: Event) => {
      const updates = (e as CustomEvent<{ full_name?: string }>).detail;
      setUserProfile((prev) => prev ? { ...prev, ...updates } : prev);
    };
    window.addEventListener('profile-updated', handler);
    return () => window.removeEventListener('profile-updated', handler);
  }, []);

  // ── Supabase realtime: keep top-bar avatar & name in sync with DB changes ──
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    const channel = supabase
      .channel(`topbar-profile-${user.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'user_profiles', filter: `id=eq.${user.id}` },
        (payload) => {
          const updated = payload.new as { full_name?: string; avatar_url?: string; role?: string };
          setUserProfile((prev) =>
            prev
              ? {
                  ...prev,
                  full_name: updated.full_name ?? prev.full_name,
                  avatar_url: updated.avatar_url ?? prev.avatar_url,
                  role: updated.role ?? prev.role,
                }
              : prev
          );
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  // ── Realtime subscriptions for instant notifications ──────────────────────
  useEffect(() => {
    if (!user) return;
    const cleanup = subscribeToRealtimeEvents({
      userId: user.id,
      role: (userProfile?.role as any) ?? role,
      onNotification: () => {},
    });
    return cleanup;
  }, [user, userProfile?.role, role]);

  const handleOpenProfile = () => {
    setProfileOpen(false);
    router.push('/profile');
  };

  const handleOpenSettings = () => {
    setProfileOpen(false);
    router.push('/profile');
  };

  const handleSignOut = async () => {
    setProfileOpen(false);
    setSigningOut(true);
    try {
      await signOut();
      router.push('/sign-up-login-screen');
    } catch (err) {
      setSigningOut(false);
    }
  };

  const displayName = userProfile?.full_name || user?.email?.split('@')[0] || 'My Account';
    const displayRole = (userProfile?.role || role) as 'sender' | 'customer' | 'traveler' | 'host' | 'admin' | 'station_moderator' | 'student' | 'courier';
  const avatarUrl = userProfile?.avatar_url;

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:relative z-50 lg:z-auto h-full
          transition-transform duration-300 ease-in-out
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          onMobileClose={() => setMobileSidebarOpen(false)}
          role={displayRole}
          displayName={displayName}
          avatarUrl={avatarUrl || undefined}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* ── Top Menu Bar ── */}
        <header className="flex items-center justify-between px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 bg-card border-b border-border z-30 shrink-0 min-h-[52px]">
          {/* Left: hamburger (mobile) + home button */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden p-2.5 rounded-lg hover:bg-muted transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Open menu"
            >
              <svg className="w-5 h-5 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Desktop sidebar toggle */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="Toggle sidebar"
            >
              <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Home button */}
            <Link
              href="/"
              className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors min-h-[44px]"
              aria-label="Home"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="hidden sm:inline">{t.home}</span>
            </Link>
          </div>

          {/* Right: language, dark/light, notifications, profile */}
          <div className="flex items-center gap-0.5 sm:gap-1">

            {/* Language selector */}
            <div className="relative hidden sm:block" ref={langRef}>
              <button
                onClick={() => { setLangOpen(!langOpen); setProfileOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                aria-label="Select language"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
                <span className="hidden sm:inline">{language}</span>
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-card border border-border rounded-xl shadow-lg py-1 z-50">
                  {supportedLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangSelect(lang.code)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors ${language === lang.code ? 'text-primary font-semibold' : 'text-foreground'}`}
                    >
                      <span className="font-mono text-xs text-muted-foreground w-6">{lang.code}</span>
                      <span>{lang.native}</span>
                      {language === lang.code && (
                        <svg className="w-3.5 h-3.5 text-primary ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dark / Light mode toggle */}
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {/* Notification bell — between dark/light toggle and messages */}
            <NotificationBell />

            {/* Message Center — Travel & Storage chats with realtime unread count */}
            <MessageCenter />

            {/* User profile */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => { setProfileOpen(!profileOpen); setLangOpen(false); }}
                className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
                aria-label="User profile"
              >
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-primary/20">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={`${displayName} profile picture`} className="w-full h-full object-cover" />
                  ) : (
                    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </div>
                <span className="hidden sm:block text-sm font-medium text-foreground max-w-[120px] truncate">{displayName}</span>
                <svg className="hidden sm:block w-3 h-3 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-1 w-56 bg-card border border-border rounded-xl shadow-lg py-1 z-50">
                  <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/20 overflow-hidden flex items-center justify-center flex-shrink-0 ring-2 ring-primary/20">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={`${displayName} profile picture`} className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                      <p className="text-xs text-muted-foreground capitalize">{displayRole}</p>
                      {user?.email && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{user.email}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleOpenProfile}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t.profile}
                  </button>
                  <button
                    onClick={handleOpenSettings}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {t.settings}
                  </button>
                  <div className="border-t border-border mt-1 pt-1">
                    <button
                      onClick={handleSignOut}
                      disabled={signingOut}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-muted transition-colors disabled:opacity-50"
                    >
                      {signingOut ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      )}
                      {signingOut ? t.loading : t.signOut}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Scrollable content area */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10 py-4 sm:py-6">
            {children}
          </div>
        </main>
      </div>

      {/* Profile / Settings Modal */}
      <ProfileSettingsModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        initialTab={profileModalTab}
      />

      {/* Onboarding Wizard — shown once per user after first login */}
      {showOnboarding && user?.id && (
        <OnboardingWizard
          role={displayRole}
          userId={user.id}
          onDismiss={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}